import { type FC, useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import HeroWorkspacePreview, { heroCodeData, heroResultData } from './landing/HeroWorkspacePreview';
import ExampleResearchOutput from './landing/ExampleResearchOutput';
import CompositeSystemMockup from './landing/CompositeSystemMockup';
import ContinuousValidationMock from './landing/ContinuousValidationMock';
import { usePageMeta } from '../hooks/usePageMeta';

/* ──────────────────────────── RESEARCH SEATS ────────────────────────────── */

// TODO: Replace with API call to fetch live seat availability
const TOTAL_SEATS = 50;
const SEATS_FILLED = 0;
const SEATS_REMAINING = TOTAL_SEATS - SEATS_FILLED;
const SEATS_AVAILABLE = SEATS_REMAINING > 0;

/* ── Scroll-reveal hook: subtle fade-up, triggers once ── */
function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible, style: { opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(18px)', transition: 'opacity 0.7s ease, transform 0.7s ease' } as React.CSSProperties };
}

const LandingPage: FC = () => {
  usePageMeta({ title: 'Quanthop \u2014 Research, Optimize & Validate Trading Strategies', description: 'A professional research environment for systematic traders. Backtest, run multi-asset optimization, and continuously validate strategies with Quanthop.' });

  /* ── Scroll-reveal refs ── */
  const rev4 = useScrollReveal(0.08);   // Adaptive Flow panel
  const rev5a = useScrollReveal(0.2);   // Who it's for - left card
  const rev5b = useScrollReveal(0.2);   // Who it's for - right card
  const rev6 = useScrollReveal(0.15);   // Research Integrity cards
  const rev7 = useScrollReveal(0.2);    // Research Access CTA

  /* ── Hero preview animation ── */
  const [heroVisibleCode, setHeroVisibleCode] = useState(0);
  const [heroVisibleResults, setHeroVisibleResults] = useState(0);
  const [heroAfProgress, setHeroAfProgress] = useState(0);
  const heroPreviewRef = useRef<HTMLDivElement>(null);
  const heroAnimStarted = useRef(false);

  const startHeroAnimation = useCallback(() => {
    if (heroAnimStarted.current) return;
    heroAnimStarted.current = true;
    const CODE_TOTAL = heroCodeData.length;
    const RESULT_TOTAL = heroResultData.length;

    // Phase 1: Type code lines
    let codeLine = 0;
    const codeTimer = setInterval(() => {
      codeLine += 1;
      setHeroVisibleCode(codeLine);
      if (codeLine >= CODE_TOTAL) {
        clearInterval(codeTimer);
        // Phase 2: Reveal results after brief pause
        setTimeout(() => {
          let resultLine = 0;
          const resultTimer = setInterval(() => {
            resultLine += 1;
            setHeroVisibleResults(resultLine);
            if (resultLine >= RESULT_TOTAL) {
              clearInterval(resultTimer);
              // Phase 3: Count up AF numbers
              const t0 = performance.now();
              const tick = (now: number) => {
                const p = Math.min((now - t0) / 800, 1);
                setHeroAfProgress(1 - Math.pow(1 - p, 3)); // easeOutCubic
                if (p < 1) requestAnimationFrame(tick);
              };
              requestAnimationFrame(tick);
            }
          }, 80);
        }, 300);
      }
    }, 90);
  }, []);

  useEffect(() => {
    const el = heroPreviewRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) startHeroAnimation(); },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [startHeroAnimation]);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ──────────── 1. HERO ──────────── */}
      <section className="relative pt-24 sm:pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div className="absolute inset-0 landing-grid-bg opacity-[0.03] pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] rounded-full bg-[#9d4edd]/[0.035] blur-[160px] pointer-events-none" />

        <div className="max-w-[780px] mx-auto relative z-10 text-center">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/60 mb-5">
            Research Workspace
          </p>

          <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.0] tracking-[-0.025em] mb-5 text-white brightness-[1.1]">
            Integrated quantitative research.
          </h1>

          <p className="text-[16px] text-gray-300/85 max-w-[600px] mx-auto mb-2 leading-[1.7]">
            Develop strategies, validate robustness, and monitor live performance inside a single research environment.
          </p>
          <p className="text-[15px] text-gray-300/90 mb-8 leading-[1.7]">
            Code-first research. Validation embedded.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2.5 px-6 py-2.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
            >
              Start Research
              <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
            </Link>
            <Link
              to="/features/research-pipeline"
              className="text-sm text-gray-400 hover:text-gray-200 hover:underline underline-offset-4 transition-colors"
            >
              View the research pipeline
            </Link>
          </div>
        </div>

        <HeroWorkspacePreview
          previewRef={heroPreviewRef}
          heroVisibleCode={heroVisibleCode}
          heroVisibleResults={heroVisibleResults}
          heroAfProgress={heroAfProgress}
        />
      </section>

      {/* ──────────── EXAMPLE RESEARCH OUTPUT ──────────── */}
      <section className="py-16 px-4 sm:px-6 lg:px-10" style={{ background: 'linear-gradient(135deg, #0d1018 0%, #100e1c 50%, #0d1018 100%)' }}>
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Example Research Output
            </p>
            <h2 className="text-[20px] md:text-[22px] font-semibold text-white tracking-[-0.02em] mb-3">
              What structured validation produces
            </h2>
            <p className="text-[14px] text-gray-400 leading-[1.7] max-w-xl mx-auto">
              A single strategy evaluated through the full research pipeline.
            </p>
          </div>

          <ExampleResearchOutput />

          <p className="text-[11px] text-gray-600 text-center mt-4 font-mono">
            Simulated output based on platform methodology. Not a performance guarantee.
          </p>
        </div>
      </section>

      {/* ──────────── RESEARCH WORKFLOW (3 columns) ──────────── */}
      <section className="pt-20 px-4 sm:px-6 lg:px-10 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          {/* Section header */}
          <div className="mb-14 text-center">
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-2">
              Research Workflow
            </p>
            <h2 className="text-[22px] md:text-[26px] font-semibold text-[#1a1a2e] tracking-[-0.02em] mb-3">
              Three stages of structured research
            </h2>
            <p className="text-[15px] text-gray-500 leading-[1.7] max-w-2xl mx-auto">
              Every strategy passes through a defined process before results are accepted.
            </p>
          </div>

          {/* 3-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">

            {/* 01 Strategy Definition */}
            <div className="bg-white border border-gray-200/80 rounded-lg p-8 flex flex-col">
              <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-3">01</p>
              <h3 className="text-[17px] font-semibold text-[#1a1a2e] tracking-[-0.01em] mb-2">
                Strategy Definition
              </h3>
              <p className="text-[14px] text-gray-500 leading-[1.65] mb-6">
                Define strategy logic, parameter bounds, and dataset scope in a structured research environment.
              </p>
              <ul className="space-y-2.5 mb-8">
                {['Strategy logic using QSL', 'Parameter configuration with optimization bounds', 'Dataset and interval selection', 'Experiment versioning'].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[13px] text-gray-600">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-[#9d4edd]/30 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/features/workspace" className="mt-auto text-[13px] font-medium text-[#9d4edd] hover:text-[#8b3ec7] transition-colors inline-flex items-center gap-1.5">
                View research environment <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
              </Link>
            </div>

            {/* 02 Validation Pipeline */}
            <div className="bg-white border border-gray-200/80 rounded-lg p-8 flex flex-col">
              <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-3">02</p>
              <h3 className="text-[17px] font-semibold text-[#1a1a2e] tracking-[-0.01em] mb-2">
                Validation Pipeline
              </h3>
              <p className="text-[14px] text-gray-500 leading-[1.65] mb-6">
                Multi-stage validation pipeline that tests strategy robustness across assets, parameters, and time.
              </p>
              <ul className="space-y-2.5 mb-8">
                {['Baseline evaluation across asset groups', 'Walk-forward analysis with rolling windows', 'Out-of-sample forward validation', 'Deployment monitoring'].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[13px] text-gray-600">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-[#9d4edd]/30 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/features/validation" className="mt-auto text-[13px] font-medium text-[#9d4edd] hover:text-[#8b3ec7] transition-colors inline-flex items-center gap-1.5">
                See validation pipeline <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
              </Link>
            </div>

            {/* 03 Stability Analysis */}
            <div className="bg-white border border-gray-200/80 rounded-lg p-8 flex flex-col">
              <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-3">03</p>
              <h3 className="text-[17px] font-semibold text-[#1a1a2e] tracking-[-0.01em] mb-2">
                Stability Analysis
              </h3>
              <p className="text-[14px] text-gray-500 leading-[1.65] mb-6">
                Structural analysis of parameter stability across optimization landscapes and time regimes.
              </p>
              <ul className="space-y-2.5 mb-8">
                {['Cluster-based stability scoring', 'Walk-forward efficiency metrics', 'Regime robustness assessment', 'Parameter drift detection'].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-[13px] text-gray-600">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-[#9d4edd]/30 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/features/optimization" className="mt-auto text-[13px] font-medium text-[#9d4edd] hover:text-[#8b3ec7] transition-colors inline-flex items-center gap-1.5">
                Explore stability analysis <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
              </Link>
            </div>

          </div>

          {/* Connector lines: cards -> mockup */}
          <div className="hidden md:grid grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex justify-center">
                <div className="flex flex-col items-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#9d4edd]/30" />
                  <div className="w-px h-8 bg-gradient-to-b from-[#9d4edd]/25 to-[#9d4edd]/05" />
                </div>
              </div>
            ))}
          </div>

          {/* Composite system mockup */}
          <CompositeSystemMockup />

          {/* Connector: mockup -> Continuous Validation */}
          <div className="flex justify-center pt-4 pb-6">
            <div className="flex flex-col items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-[#9d4edd]/30" />
              <div className="w-px h-8 bg-gradient-to-b from-[#9d4edd]/25 to-[#9d4edd]/05" />
            </div>
          </div>

        </div>
      </section>

      {/* ──────────── 4. CONTINUOUS VALIDATION ──────────── */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-10 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d1018 0%, #100e1c 50%, #0d1018 100%)' }}>
        <div className="absolute inset-0 bg-[#9d4edd]/[0.02] pointer-events-none" />
        <div className="max-w-[1100px] mx-auto relative z-10">
          <div className="text-center mb-10">
            <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/40 mb-1">
              04 &mdash; Live Validation
            </p>
            <h2 className="text-[22px] md:text-[24px] font-bold text-white tracking-[-0.02em] mb-6">
              Continuous validation
            </h2>
            <div className="max-w-xl mx-auto space-y-3 text-[15px] text-gray-300/75 leading-[1.7]">
              <p>
                Backtests provide a historical snapshot.
                Strategies evolve.
              </p>
              <p>
                Adaptive Flow extends validation using rolling windows,
                health scoring and controlled re-optimization.
              </p>
            </div>
          </div>

          <ContinuousValidationMock scrollRef={rev4.ref} scrollStyle={rev4.style} />
          <p className="text-[10px] text-gray-400/70 mt-3 font-mono text-center">
            Live validation with health scoring, risk tolerance and controlled re-optimization
          </p>

          <div className="text-center mt-12 max-w-lg mx-auto">
            <p className="text-[17px] text-gray-200/75 leading-[1.5] font-medium">
              The goal is not peak performance.
            </p>
            <p className="text-[17px] text-gray-200/75 leading-[1.5] font-medium">
              The goal is controlled robustness over time.
            </p>

            <Link to="/features/continuous-validation" className="inline-flex items-center gap-2 mt-8 text-[13px] font-medium text-[#9d4edd] hover:text-[#b266ff] transition-colors">
              Learn more <FontAwesomeIcon icon={faArrowRight} className="text-[11px]" />
            </Link>
          </div>
        </div>
      </section>

      {/* ──────────── 5. WHO IT'S FOR ──────────── */}
      <section className="py-20 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 mb-4">
              Who This Is For
            </p>
            <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-4">
              Designed for systematic researchers
            </h2>
            <p className="text-[15px] text-gray-300/70 leading-[1.7] max-w-[600px] mx-auto">
              Quanthop is a research environment for building and validating
              strategies with structural discipline &mdash; not a signal feed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* For */}
            <div ref={rev5a.ref} style={{ ...rev5a.style, background: 'rgba(34, 197, 94, 0.03)' }} className="rounded-lg border border-white/[0.06] px-5 py-5">
              <h3 className="text-[12px] font-semibold text-emerald-400/80 uppercase tracking-wider mb-4">Quanthop is for</h3>
              <div className="space-y-3">
                {[
                  'Quantitative developers who write and own their strategy logic',
                  'Researchers who prioritize robustness over peak backtests',
                  'Traders who use walk-forward and out-of-sample validation as standard practice',
                  'Builders who test across assets, regimes and parameter regions',
                  'Anyone who wants continuous monitoring after deployment',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <span className="text-emerald-400/60 text-[11px] mt-[2px] flex-shrink-0">&#10003;</span>
                    <span className="text-[13px] text-gray-300/85 leading-snug">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Not for */}
            <div ref={rev5b.ref} style={{ ...rev5b.style, background: 'rgba(255, 255, 255, 0.015)', transitionDelay: '0.15s' }} className="rounded-lg border border-white/[0.06] px-5 py-5">
              <h3 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-4">Quanthop is not for</h3>
              <div className="space-y-3">
                {[
                  'Buy/sell signal seekers',
                  'Fully automated trading bots \u2014 execution is not the product',
                  'One-click strategies with guaranteed returns',
                  'Discord or Telegram-style indicator packs',
                  "Users who don't validate results beyond a single backtest",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <span className="text-gray-600 text-[11px] mt-[2px] flex-shrink-0">&mdash;</span>
                    <span className="text-[13px] text-gray-500/80 leading-snug">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center text-[13px] text-gray-400/60 mt-8 leading-relaxed max-w-[640px] mx-auto">
            If terms like <span className="text-gray-300/70 italic">walk-forward analysis</span>, <span className="text-gray-300/70 italic">parameter stability</span> and <span className="text-gray-300/70 italic">out-of-sample validation</span> are already part of your workflow, you&apos;ll feel at home.
          </p>
        </div>
      </section>

      {/* ──────────── 6. RESEARCH PHILOSOPHY ──────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40 bg-[#0d1117]">
        <div className="max-w-[700px] mx-auto text-center">
          <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 mb-5">
            Research Philosophy
          </p>
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-10">
            Validation must be structural, not optional
          </h2>
          <div className="max-w-[580px] mx-auto space-y-6 text-[15px] leading-[1.75]">
            <p className="text-gray-300/65">
              Markets change. Strategies degrade.
            </p>
            <p className="text-gray-300/65">
              Robust research requires more than backtesting.
            </p>
            <p className="text-gray-200/85">
              Quanthop is built around one principle:
            </p>
            <p className="text-[17px] text-white font-medium">
              Every strategy passes through a research pipeline designed to detect fragility before capital is deployed.
            </p>
            <p className="text-gray-300/65">
              Test first. Validate structurally. Deploy with discipline.
            </p>
          </div>
        </div>
      </section>

      {/* ──────────── 6b. RESEARCH INTEGRITY ──────────── */}
      <section className="py-24 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#9d4edd]/40 mb-4">
              Research Integrity
            </p>
            <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
              Guardrails built into the platform
            </h2>
            <p className="text-[15px] text-gray-300/70 leading-[1.7] max-w-[600px] mx-auto">
              Every stage of the research pipeline is designed to prevent common
              research mistakes that lead to fragile strategies.
            </p>
          </div>

          <div ref={rev6.ref} style={rev6.style} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                title: 'Out-of-sample validation',
                description: 'Walk-forward testing separates training and validation windows so performance is evaluated on unseen data.',
              },
              {
                title: 'Parameter stability scoring',
                description: 'Optimization results are analysed for stable parameter regions rather than isolated peaks.',
              },
              {
                title: 'Cross-asset validation',
                description: 'Strategies are tested across multiple assets to detect overfitting to a single market.',
              },
              {
                title: 'Continuous health monitoring',
                description: 'Adaptive Flow tracks strategy behaviour after deployment and detects degradation over time.',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="rounded-lg border border-white/[0.06] px-5 py-5"
                style={{ background: 'rgba(157, 78, 221, 0.03)' }}
              >
                <h3 className="text-[13px] font-semibold text-white tracking-[-0.005em] mb-2">{card.title}</h3>
                <p className="text-[13px] text-gray-400/80 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────── 7. RESEARCH ACCESS ──────────── */}
      <section className="relative py-28 px-4 sm:px-6 lg:px-10 border-t border-gray-700/40 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
        <div ref={rev7.ref} style={rev7.style} className="max-w-[600px] mx-auto text-center relative z-10">
          <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-6">
            Research access
          </h2>

          {SEATS_AVAILABLE ? (
            <>
              <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
                Quanthop is released through controlled research seats to ensure
                platform stability during early deployment.
              </p>
              <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-10">
                Access expands gradually as infrastructure capacity increases and
                research workflows are validated under real workloads.
              </p>

              {/* Cohort label */}
              <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#9d4edd]/50 mb-4">
                Cohort 01 &mdash; Initial Research Access
              </p>

              {/* Seat meter */}
              <div className="max-w-[320px] mx-auto mb-3">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(SEATS_FILLED / TOTAL_SEATS) * 100}%`,
                      background: 'linear-gradient(90deg, #9d4edd, #b266ff)',
                    }}
                  />
                </div>
              </div>
              <p className="text-[13px] font-mono text-gray-300/60 mb-10">
                {SEATS_FILLED} / {TOTAL_SEATS} research seats filled
              </p>

              <Link
                to="/pricing"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
              >
                Start Research
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>

              <p className="text-[11px] text-gray-500/50 mt-6 font-mono">
                Current cohort includes independent researchers, systematic traders and quantitative developers.
              </p>
            </>
          ) : (
            <>
              <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
                All research seats in the current cohort are filled.
              </p>
              <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-10">
                Access will reopen when the next cohort is released.
              </p>

              <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-gray-500/50 mb-6">
                Cohort 01 &mdash; Full
              </p>

              <Link
                to="/waitlist"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 border border-[#9d4edd]/40 hover:border-[#9d4edd]/70 text-gray-200 rounded text-sm font-medium transition-colors"
              >
                Join Waiting List
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
              </Link>
            </>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
